import { Controller } from "@hotwired/stimulus"
import Autocomplete from "bootstrap5-autocomplete"
import { search_activities_companies_path } from "../routes"

// Connects to data-controller="company-activities"
export default class extends Controller {
  static targets = ["search", "list", "template", "emptyState"]
  static values = {
    selected: Array
  }

  connect() {
    // Initialize selectedSet from already rendered items
    const existingItems = Array.from(this.listTarget.querySelectorAll('[data-activity-id]'))
    this.selectedSet = new Set(existingItems.map(item => Number(item.dataset.activityId)))
    
    // Populate activitiesById from rendered items
    this.activitiesById = new Map()
    existingItems.forEach(item => {
      const id = Number(item.dataset.activityId)
      const labelElement = item.querySelector('[data-company-activities-role="label"], .fw-semibold')
      const categoryElement = item.querySelector('[data-company-activities-role="category"], .badge')
      if (labelElement && categoryElement) {
        this.activitiesById.set(id, {
          id: id,
          name: labelElement.textContent.trim(),
          category: categoryElement.textContent.trim(),
          label: `${categoryElement.textContent.trim()} – ${labelElement.textContent.trim()}`
        })
      }
    })

    this.updateEmptyState()
    this.shouldShowOnFocus = false
    this.initAutocomplete()

    this.focusHandler = this.handleFocus.bind(this)
    this.inputHandler = this.handleInput.bind(this)
    this.searchTarget.addEventListener("focus", this.focusHandler)
    this.searchTarget.addEventListener("input", this.inputHandler)
  }

  disconnect() {
    this.searchTarget.removeEventListener("focus", this.focusHandler)
    this.searchTarget.removeEventListener("input", this.inputHandler)
    this.disposeAutocomplete()
  }


  initAutocomplete() {
    this.disposeAutocomplete()

    this.autocomplete = new Autocomplete(this.searchTarget, {
      server: search_activities_companies_path(),
      liveServer: true,
      serverMethod: "GET",
      queryParam: "q",
      serverDataKey: "data",
      labelField: "label",
      valueField: "id",
      searchFields: ["name", "label", "category"],
      highlightTyped: false,
      fullWidth: true,
      fixed: false,
      maximumItems: 0,
      showAllSuggestions: false,
      suggestionsThreshold: 1,
      startsWith: false,
      autoselectFirst: false,
      ignoreEnter: false,
      debounceTime: 300,
      onSelectItem: ({ value, label, name, category }) => {
        this.addById(value, { name, category, label })
        this.searchTarget.value = ""
        if (this.autocomplete) {
          this.autocomplete.hideSuggestions()
          this.autocomplete.removeSelection?.()
          requestAnimationFrame(() => {
            if ((this.searchTarget.value || "").trim().length > 0) {
              this.autocomplete?.showOrSearch(true)
            }
          })
        }
      },
      onRenderItem: (item, label, instance) => {
        const query = (instance?._searchInput?.value || "").trim()
        // Remove any existing HTML tags from the label to avoid duplication
        const text = (label || item?.label || "").replace(/<[^>]*>/g, "")

        if (!query) {
          return text
        }

        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        const highlightClass = instance?._config?.highlightClass || "bg-warning"
        return text.replace(
          new RegExp(`(${escapedQuery})`, "ig"),
          `<mark class="${highlightClass}">$1</mark>`
        )
      }
    })
  }

  disposeAutocomplete() {
    if (this.autocomplete && typeof this.autocomplete.dispose === "function") {
      this.autocomplete.dispose()
    }
    this.autocomplete = null
  }

  handleFocus() {
    if (this.autocomplete) {
      const hasSearch = (this.searchTarget.value || "").trim().length > 0
      if (hasSearch) {
        this.autocomplete.showOrSearch(true)
      } else {
        this.autocomplete.hideSuggestions()
      }
      if (typeof this.autocomplete.removeSelection === "function") {
        this.autocomplete.removeSelection()
      }
    }
    const length = this.searchTarget.value.length
    try {
      this.searchTarget.setSelectionRange(length, length)
    } catch (_) {
      // some input types (e.g., number) don't support setSelectionRange
    }
  }

  handleInput() {
    if (this.autocomplete) {
      const hasSearch = (this.searchTarget.value || "").trim().length > 0
      if (hasSearch) {
        this.autocomplete.showOrSearch(true)
      } else {
        this.autocomplete.hideSuggestions()
      }
    }
  }

  handleEnter(event) {
    if (this.autocomplete?.getSelection?.()) {
      return
    }

    if ((this.searchTarget.value || "").trim().length === 0) {
      return
    }

    event.preventDefault()
    // Let autocomplete handle the selection
    const selection = this.autocomplete?.getSelection?.()
    if (selection) {
      selection.click()
    }
  }


  addById(id, activityData = null) {
    const numericId = Number(id)
    if (Number.isNaN(numericId) || this.selectedSet.has(numericId)) {
      return
    }

    // If activity data is provided, store it
    if (activityData) {
      this.activitiesById.set(numericId, {
        id: numericId,
        name: activityData.name,
        category: activityData.category,
        label: activityData.label
      })
    }

    const activity = this.activitiesById.get(numericId)
    if (!activity) {
      // If we don't have the activity data, try to fetch it
      this.fetchActivityById(numericId).then(() => {
        this.createListItem(numericId)
        this.updateEmptyState()
      })
      return
    }

    this.selectedSet.add(numericId)
    this.createListItem(numericId)
    this.updateEmptyState()
  }

  async fetchActivityById(id) {
    try {
      const response = await fetch(`${search_activities_companies_path()}?q=&id=${id}`)
      const data = await response.json()
      const activity = data.data.find((item) => Number(item.id) === Number(id))
      if (activity) {
        this.activitiesById.set(Number(id), {
          id: Number(activity.id),
          name: activity.name,
          category: activity.category,
          label: activity.label
        })
      }
    } catch (error) {
      console.error("Error fetching activity:", error)
    }
  }

  createListItem(id) {
    const activity = this.activitiesById.get(id)
    if (!activity) {
      return
    }

    if (this.listTarget.querySelector(`[data-activity-id="${id}"]`)) {
      return
    }

    const item = this.templateTarget.content.firstElementChild.cloneNode(true)
    item.dataset.activityId = String(id)

    const hiddenField = item.querySelector('input[type="hidden"]')
    if (hiddenField) {
      hiddenField.value = id
    }

    const labelElement = item.querySelector('[data-company-activities-role="label"]')
    if (labelElement) {
      labelElement.textContent = activity.name
    }

    const categoryElement = item.querySelector('[data-company-activities-role="category"]')
    if (categoryElement) {
      categoryElement.textContent = activity.category
    }

    this.listTarget.appendChild(item)
  }

  remove(event) {
    event.preventDefault()
    const item = event.currentTarget.closest("[data-activity-id]")
    if (!item) {
      return
    }

    const id = Number(item.dataset.activityId)
    this.selectedSet.delete(id)
    item.remove()
    this.updateEmptyState()
  }

  updateEmptyState() {
    if (!this.hasEmptyStateTarget) {
      return
    }

    const isEmpty = this.listTarget.children.length === 0
    this.emptyStateTarget.classList.toggle("d-none", !isEmpty)
  }

}

